import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import Notification from "../../components/Notification/Notification";
// import Spinner from "../../components/Spinner/Spinner";
import * as XLSX from "xlsx";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import { FiPlus, FiMinus } from "react-icons/fi";
import "./AdministrativeUnits.css";

/* ─── UnitRow ─────────────────────────────────────────────────── */
const UnitRow = ({ node, level = 1, refresh, setNotification, openConfirm }) => {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [description, setDescription] = useState(node.description || "");
    const [remark, setRemark] = useState(node.remark || "");

    useEffect(() => { setEditing(false); }, [node.description, node.remark]);

    const hasChildren = node.children && node.children.length > 0;

    const handleSaveClick = () => {
        if (description.length > 100 || remark.length > 100) {
            setNotification({ show: true, message: "Description and Remark must not exceed 100 characters", type: "error" });
            return;
        }
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        openConfirm({ open: true, token, nodeId: node._id, payload: { description, remark } });
    };

    return (
        <>
            <tr className={`au-level-${level}`}>
                {/* CODE */}
                <td style={{ paddingLeft: `${(level - 1) * 22 + 16}px` }}>
                    <div className="au-code-cell">
                        {hasChildren && (
                            <button className="au-toggle-btn" onClick={() => setExpanded(p => !p)}>
                                {expanded ? <FiMinus size={10} /> : <FiPlus size={10} />}
                            </button>
                        )}
                        <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 13, color: "var(--forest)", letterSpacing: -0.2 }}>
                            {node.code}
                        </span>
                    </div>
                </td>

                {/* DESCRIPTION */}
                <td>
                    {editing
                        ? <input className="au-edit-input" value={description} onChange={e => setDescription(e.target.value)} />
                        : <span style={{ color: "var(--text-mid)" }}>{node.description || <span style={{ color: "var(--muted-light)" }}>—</span>}</span>
                    }
                </td>

                {/* REMARK */}
                <td>
                    {editing
                        ? <input className="au-edit-input" value={remark} onChange={e => setRemark(e.target.value)} />
                        : <span style={{ color: "var(--muted)" }}>{node.remark || <span style={{ color: "var(--muted-light)" }}>—</span>}</span>
                    }
                </td>

                {/* STATUS */}
                <td>
                    <span className={`au-status ${node.isActive ? "active" : "inactive"}`}>
                        <span className="au-status-dot" />
                        {node.isActive ? "Active" : "Inactive"}
                    </span>
                </td>

                {/* ACTIONS */}
                <td>
                    <div className="au-action-wrap">
                        {!editing ? (
                            <button className="au-icon-btn" title="Edit" onClick={() => setEditing(true)}>✏️</button>
                        ) : (
                            <>
                                <button className="au-icon-btn save" title="Save" onClick={handleSaveClick}>💾</button>
                                <button className="au-icon-btn cancel" title="Cancel" onClick={() => setEditing(false)}>✕</button>
                            </>
                        )}
                    </div>
                </td>
            </tr>

            {expanded && node.children.map(child => (
                <UnitRow key={child._id} node={child} level={level + 1}
                    refresh={refresh} setNotification={setNotification} openConfirm={openConfirm} />
            ))}
        </>
    );
};

/* ─── Main Component ──────────────────────────────────────────── */
const AdministrativeUnits = () => {
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [scope, setScope] = useState("state");
    const [notification, setNotification] = useState({ show: false, message: "", type: "info" });
    const [confirmState, setConfirmState] = useState({ open: false, token: "", nodeId: null, payload: null });
    const [filters, setFilters] = useState({ code: "", level: "", isActive: "" });

    const cleanCode = (val) => String(val || "").replace(/[^0-9A-Za-z]/g, "").trim();

    const fetchTree = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({ ...filters, scope }).toString();
            const res = await privateApi.get(`/api/administrative-units?${query}`);
            setTreeData(res?.data?.data || []);
        } catch {
            setNotification({ show: true, message: "Failed to load data", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initialFetchTree = async () => {
            try {
                setLoading(true);
                const query = new URLSearchParams({ ...filters, scope }).toString();
                const res = await privateApi.get(`/api/administrative-units?${query}`);
                setTreeData(res?.data?.data || []);
            } catch {
                setNotification({ show: true, message: "Failed to load data", type: "error" });
            } finally {
                setLoading(false);
            }
        };

        initialFetchTree();
    }, [filters, scope]);

    const processFile = (f) => {
        if (!f) return;
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
            const json = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: "" });
            const normalized = json.map(item => {
                const n = {};
                Object.keys(item).forEach(k => { n[k.trim().toUpperCase()] = item[k]; });
                return {
                    sectorCode: cleanCode(n.SECTOR),
                    organizationCode: cleanCode(n.ORGANISATION),
                    subOrganizationCode: cleanCode(n["SUB-ORG"]),
                    subSubOrganizationCode: cleanCode(n["SUB-SUB-ORG"]),
                    subSubSubOrganizationCode: cleanCode(n["SUB-SUB-SUB-ORG"]),
                    description: String(n.DESCRIPTION || "").trim(),
                    remark: String(n.REMARK || "").trim(),
                };
            });
            setPreviewData(normalized.slice(0, 10));
        };
        reader.readAsArrayBuffer(f);
    };

    const handleUpload = async () => {
        if (!file) { setNotification({ show: true, message: "Select or drop a file first", type: "error" }); return; }
        try {
            setUploading(true);
            const formData = new FormData();
            formData.append("file", file);
            formData.append("scope", scope);
            const res = await privateApi.post(`/api/administrative-units/upload-${scope}`, formData, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 0,
            });
            setNotification({ show: true, message: `Uploaded ${res.data.count} records successfully`, type: "success" });
            setFile(null);
            setPreviewData([]);
            fetchTree();
        } catch {
            setNotification({ show: true, message: "Upload error", type: "error" });
        } finally {
            setUploading(false);
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const query = new URLSearchParams({ ...filters, scope }).toString();
            const response = await privateApi.get(`/api/administrative-units/export?${query}`, { responseType: "blob" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8;" }));
            link.download = "AdministrativeUnits.csv";
            link.click();
        } catch {
            setNotification({ show: true, message: "Export failed", type: "error" });
        } finally {
            setExporting(false);
        }
    };

    const handleConfirm = async (input) => {
        if (input !== confirmState.token) {
            setNotification({ show: true, message: "Incorrect confirmation code", type: "error" });
            setConfirmState({ open: false, token: "", nodeId: null, payload: null });
            return;
        }
        try {
            await privateApi.patch(`/api/administrative-units/${confirmState.nodeId}`, confirmState.payload);
            setNotification({ show: true, message: "Administrative unit updated successfully", type: "success" });
            fetchTree();
        } catch {
            setNotification({ show: true, message: "Update failed", type: "error" });
        } finally {
            setConfirmState({ open: false, token: "", nodeId: null, payload: null });
        }
    };

    return (
        <div className="au-page">
            <Notification show={notification.show} message={notification.message}
                type={notification.type} onClose={() => setNotification(p => ({ ...p, show: false }))} />

            {/* HEADER */}
            <header className="au-header">
                <div className="au-header-left">
                    <h1>Administrative Units</h1>
                    <p>Manage government organisational hierarchy and classification codes</p>
                </div>
                <div className="au-header-actions">
                    <select className="au-scope-select" value={scope} onChange={e => setScope(e.target.value)}>
                        <option value="state">State</option>
                        <option value="lga">LGA</option>
                    </select>
                    <Link className="au-btn au-btn-primary" to="/administrative-units/create">+ Add Unit</Link>
                    <button className="au-btn au-btn-secondary" onClick={handleExport} disabled={exporting}>
                        {exporting ? "Exporting…" : "⬇ Export CSV"}
                    </button>
                </div>
            </header>

            {/* FILTERS */}
            <div className="au-filter-bar">
                <span className="au-filter-label">Filter</span>
                <input className="au-filter-input" name="code" placeholder="Search by code…"
                    value={filters.code} onChange={e => setFilters(p => ({ ...p, code: e.target.value }))} />
                <select className="au-filter-select" name="level" value={filters.level}
                    onChange={e => setFilters(p => ({ ...p, level: e.target.value }))}>
                    <option value="">All Levels</option>
                    {[1, 2, 3, 4, 5].map(l => <option key={l} value={l}>Level {l}</option>)}
                </select>
                <select className="au-filter-select" name="isActive" value={filters.isActive}
                    onChange={e => setFilters(p => ({ ...p, isActive: e.target.value }))}>
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                </select>
            </div>

            {/* UPLOAD */}
            <div className="au-upload-section">
                <div className={`au-dropzone ${dragging ? "dragging" : ""}`}
                    onDrop={e => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files[0]); }}
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                >
                    <span className="au-dropzone-icon">📂</span>
                    <p>Drag &amp; drop Excel file here</p>
                    <span>or click to browse (.xlsx, .xls)</span>
                    {file && <div className="au-file-name">📄 {file.name}</div>}
                    <input type="file" accept=".xlsx,.xls" onChange={e => processFile(e.target.files[0])} />
                </div>
                <div className="au-upload-actions">
                    <button className="au-btn au-btn-blue" onClick={handleUpload} disabled={uploading || !file}>
                        {uploading ? "Uploading…" : "⬆ Upload File"}
                    </button>
                </div>
            </div>

            {/* PREVIEW */}
            {previewData.length > 0 && (
                <div className="au-preview">
                    <div className="au-preview-title">
                        Preview <span className="au-preview-badge">First 10 rows</span>
                    </div>
                    <div className="au-table-wrap">
                        <table className="au-shared-table">
                            <thead>
                                <tr>
                                    {["Sector", "Organisation", "Sub-Org", "Sub-Sub-Org", "Sub-Sub-Sub-Org", "Description", "Remark"].map(h => (
                                        <th key={h}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previewData.map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.sectorCode || "—"}</td>
                                        <td>{row.organizationCode || "—"}</td>
                                        <td>{row.subOrganizationCode || "—"}</td>
                                        <td>{row.subSubOrganizationCode || "—"}</td>
                                        <td>{row.subSubSubOrganizationCode || "—"}</td>
                                        <td>{row.description || "—"}</td>
                                        <td>{row.remark || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* MAIN TABLE */}
            <div className="au-table-card">
                {loading ? (
                    <div className="au-loading-wrap">
                        <div className="au-spinner" />
                        <p className="au-loading-text">Loading Administrative Units…</p>
                    </div>
                ) : (
                    <div className="au-table-wrap">
                        <table className="au-table">
                            <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Description</th>
                                    <th>Remark</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {treeData.map(node => (
                                    <UnitRow key={node._id} node={node}
                                        refresh={fetchTree} setNotification={setNotification}
                                        openConfirm={setConfirmState} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {confirmState.open && (
                <ConfirmDialog show={confirmState.open} token={confirmState.token}
                    action="You are about to update this administrative unit"
                    onConfirm={handleConfirm}
                    onCancel={() => setConfirmState({ open: false, token: "", nodeId: null, payload: null })} />
            )}
        </div>
    );
};

export default AdministrativeUnits;
